// External Packages
import { useState, useContext, useEffect } from "react";

// Internal Components
import { UtilityContext } from "../../context/util-context";
import Heading from "../UI/Heading";
import { requestAdminAPI } from "../../api/posh-api";
import { MdAdd, MdRefresh } from "react-icons/md";
import { useLocation } from "react-router-dom";
import SearchBar from "../UI/SearchBar";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import PaginationMenu from "../UI/PaginationMenu";
import ExportCsvButton from "../UI/ExportCsvButton";
import { FaUserCircle } from "react-icons/fa";
import FilterMenu from "../UI/FilterMenu";
import { BiSolidMegaphone } from "react-icons/bi";
import { DataContext } from "../../context/data-context";
import AddEmployeeModal from "./AddEmployeeModal";
import Departments from "./departments";
import { isoDateToString } from "../../util/format-date";
import ListLabels from "../UI/ListLabels";
import DateFilter from "../UI/DateFilter";

/** ========================================================================
 * * Employees Page
 * Renders a page, which displays all employees.
 * ========================================================================== */

export default function Employees() {
  const { navigate } = useContext(UtilityContext);
  const { locations } = useContext(DataContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filter
  const defaultFilters = {
    query: "",
    id: "",
    status: "active",
    location: "",
    department: "",
    attendance: "all",
    sort: "name",
    sd: "",
    ed: "",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key]),
  );

  // Initialize States
  const [employees, setEmployees] = useState([]);
  const [limit, setLimit] = useState(100);
  const [filter, setFilter] = useState(paramFilters);
  const [loading, setLoading] = useState(true);
  const [manage, setManage] = useState();

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);

      // Send Request
      const data = await requestAdminAPI("search", {
        table: "employee",
        limit,
        filters: filter,
      });

      // Replace URL
      let url = "/employees?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      // Set Data
      setEmployees(data);

      // If an employee is selected
      if (filter.id) setManage(data.find((emp) => emp.id === filter.id));
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Filter Options
  const filterOptions = [
    {
      label: "Status",
      options: [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
        { label: "All", value: "all" },
      ],
      selected: filter.status,
    },
    {
      label: "Attendance",
      options: [
        { label: "All", value: "all" },
        { label: "Checked In", value: "in" },
        { label: "Checked Out", value: "out" },
      ],
      selected: filter.attendance,
    },
    {
      label: "Location",
      options: [
        ...locations.map((loc) => ({
          label: loc.name,
          value: loc.id,
        })),
      ],
      selected: filter.location,
      type: "multiple",
    },
    {
      label: "Department",
      options: [...Departments.departments],
      selected: filter.department,
      type: "multiple",
    },
  ];

  // Apply Filters
  useEffect(() => {
    fetchRecords();
  }, [filter, limit]);

  return (
    <div className="container grid">
      <AddEmployeeModal
        open={manage ? true : false}
        setOpen={setManage}
        employee={manage}
        onComplete={fetchRecords}
      />
      <Heading title="Employees" isBackArrow={true}>
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
        <FilterMenu
          options={filterOptions}
          onSubmit={(f) => setFilter({ ...filter, ...f })}
        />
        <MdAdd
          onClick={() => setManage("add")}
          className="text-3xl icon"
          title="Add Employee"
        />
        <BiSolidMegaphone
          onClick={() => navigate(`/announcements/add${search}table=employee`)}
          className="text-2xl icon"
        />
        <MdRefresh onClick={fetchRecords} className="icon" title="Refresh" />
        <ExportCsvButton array={employees} filename="employees" />
      </Heading>

      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />

      {loading ? (
        <LoadingCard columns={5} max={6} />
      ) : employees.length > 0 ? (
        <div className="list">
          <div className="labels lg:grid-cols-6">
            <ListLabels
              labels={[
                { key: "name", name: "Name/Title", classes: "lg:col-span-2" },
                "Contact Details",
                { name: "Date Added", key: "date_created" },
                { key: "type", name: "Department/Location" },
                "Attendance",
              ]}
              selected={filter.sort}
              setSelected={(sort) => setFilter({ ...filter, sort })}
            />
          </div>
          {employees.map((employee, idx) => (
            <div
              key={idx}
              onClick={() => navigate(`/employees/info?id=${employee.id}`)}
              className={
                (employee.active ? "" : "border-red-800 ") +
                "grid items-center grid-cols-2 text-sm pill lg:grid-cols-6 text-stone-200"
              }
            >
              <div className="flex items-center gap-4 lg:col-span-2">
                {employee.photo ? (
                  <img
                    src={employee.photo}
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <FaUserCircle className="w-10 h-10 text-primary" />
                )}
                <div className="grid">
                  <p className="font-semibold text-stone-200">
                    {employee.name}
                  </p>
                  <p className="text-stone-400">{employee.title}</p>
                </div>
              </div>
              <div className="grid text-stone-400">
                <p className="hidden lg:block">{employee.phone}</p>
                <p className="hidden lg:block">{employee.email}</p>
              </div>

              <p className="">{isoDateToString(employee.dateCreated)}</p>

              <div className="grid">
                <p className="font-semibold">{employee.type.toUpperCase()}</p>
                <p className="text-stone-400">{employee.location}</p>
              </div>
              <p
                className={`hidden lg:block ${employee.checkedIn ? "text-green-500" : "text-red-500"}`}
              >
                {employee.checkedIn ? "Checked In" : "Not Checked In"}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        limit={limit}
        setLimit={setLimit}
        count={employees.length}
        loading={loading}
      />
    </div>
  );
}
